import React, { useCallback, useEffect, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { useProjects } from "../../shared/projectPicker/ProjectsContext";
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    makeStyles,
    tokens,
    typographyStyles,
    Tooltip,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { NewformaMessageBar } from "../../_base/MessageBar";
import { ProjectPickerFluent9 } from "../../shared/projectPicker/ProjectPickerFluent9";
import { SmartFilingManagerFluent9 } from "@src/services/SmartFiling/SmartFilingManagerFluent9";
import { Logger } from "@src/services/Logger";
import { InvalidateCacheService } from "@src/services/NewformaApi/InvalidateCacheService";
import { OfficeWrapper } from "@src/services/OfficeWrapper";
import { IProjectsService } from "@src/services/NewformaApi/IProjectsService";
import { IProjectData } from "@src/models/shared/IProjectData";
import { FileWithId } from "@src/models/shared/FileWithId";
import { ProjectFilesBrowser } from "./ProjectFilesBrowser";
import { ProjectFolderErrorKind, ProjectFolderErrorContext } from "../../../hooks/useProjectFolderSelection";
import { MAX_ATTACHMENTS } from "../../shared/attachments/attachmentList/AttachmentListHelpers";

const useStyles = makeStyles({
    surface: {
        maxWidth: "479px",
        minWidth: "unset",
        width: "calc(100% - 20px)",
        padding: "24px",
        margin: "auto",
    },
    title: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase500,
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },
    actions: {
        width: "100%",
    },
    attachButton: {
        width: "100%",
    },
    tooltipTarget: {
        display: "block",
        width: "100%",
    },
    errorBarWrapper: {
        minWidth: 0,
        width: "100%",
    },
    remainingCount: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground2,
    },
    messageBarTitle: {
        ...typographyStyles.caption1Strong,
        color: tokens.colorNeutralForeground1,
        display: "block",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
    },
    messageBarInfo: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground1,
        display: "block",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
    },
});

interface ProjectFilesDialogProps extends LocalizeContextProps {
    isOpen: boolean;
    onClose: () => void;
    onAttach: (attachments: FileWithId[]) => void;
    smartFilingManager: SmartFilingManagerFluent9;
    invalidateCacheService: InvalidateCacheService;
    logger: Logger;
    officeWrapper: OfficeWrapper;
    projectsService: IProjectsService;
    /** Number of files already attached outside this dialog (e.g. from previous sessions). */
    existingAttachmentCount?: number;
    /**
     * Mixed-format lookup set used to prevent re-adding already-attached files. Each entry
     * is one of three key shapes, matching the format built by ProjectFilesSection:
     *   - File NRN (e.g. "nrn:file:abc123")          — same-project flat-file duplicate
     *   - Bare filename (e.g. "report.pdf")            — cross-project same-name duplicate
     *   - displayPath (e.g. "FolderA\\report.pdf")    — folder-fetched file duplicate
     * Do NOT populate this with only NRNs — that silently breaks the other two checks.
     */
    existingFileIds?: ReadonlySet<string>;
}

export const ProjectFilesDialog = withLocalize((props: ProjectFilesDialogProps) => {
    const styles = useStyles();
    const existingCount = props.existingAttachmentCount ?? 0;
    const effectiveMaxFiles = Math.max(0, MAX_ATTACHMENTS - existingCount);
    const isCapAlreadyReached = effectiveMaxFiles === 0;

    const [selectedProject, setSelectedProject] = useState<IProjectData | undefined>(undefined);
    const [selectedFiles, setSelectedFiles] = useState<FileWithId[]>([]);
    // Shadow ref so handleBrowserError can read the current count without
    // being recreated on every selection change.
    const selectedFilesRef = useRef<FileWithId[]>([]);
    selectedFilesRef.current = selectedFiles;
    const [browserResetKey, setBrowserResetKey] = useState(0);
    // Derive loading state directly from context instead of mirroring it via a
    // callback prop on ProjectPickerFluent9 — ProjectFilesDialog is always
    // rendered inside ProjectsProvider so context is guaranteed to be non-null.
    const projectsContext = useProjects();
    const isProjectsLoading = projectsContext?.loading ?? false;
    // Tracks whether a user-initiated reload is in progress so we only reset
    // the browser after the projects list has actually refreshed.
    const reloadPendingRef = useRef(false);
    /** Populated by the browser with the hook's clearError; called when the error banner is dismissed. */
    const browserClearErrorRef = useRef<(() => void) | null>(null);
    const handleBrowserClearError = useCallback((clearFn: () => void) => {
        browserClearErrorRef.current = clearFn;
    }, []);

    // Increment browserResetKey only after a reload-triggered loading cycle completes.
    useEffect(() => {
        if (!isProjectsLoading && reloadPendingRef.current) {
            reloadPendingRef.current = false;
            setBrowserResetKey((k) => k + 1);
        }
    }, [isProjectsLoading]);
    const [showProjectError, setShowProjectError] = useState<boolean>(false);
    const [isBrowserLoading, setIsBrowserLoading] = useState(false);
    /** Single error banner — latest error wins; null when dismissed or no error. */
    const [currentError, setCurrentError] = useState<{
        kind: ProjectFolderErrorKind;
        message: string;
        info?: string;
        intent: "error" | "warning";
        countAtError: number;
    } | null>(null);

    /** Maps a hook error kind to translated display text. Stable reference via useCallback. */
    const mapErrorKind = useCallback(
        (
            kind: ProjectFolderErrorKind,
            countAtError: number,
            context?: ProjectFolderErrorContext | null
        ): {
            kind: ProjectFolderErrorKind;
            message: string;
            info?: string;
            intent: "error" | "warning";
            countAtError: number;
        } => {
            switch (kind) {
                case "fileLimit": {
                    const remaining = Math.max(0, effectiveMaxFiles - countAtError);
                    const folderName = context?.folderName;
                    const info = isCapAlreadyReached
                        ? props.translate("APP.DIALOG.PROJECT_FILES.FILE_LIMIT_ALREADY_REACHED_BODY").toString()
                        : folderName
                        ? props
                              .translate("APP.DIALOG.PROJECT_FILES.FILE_LIMIT_FOLDER_BODY")
                              .toString()
                              // Replacer fn so "$&"/"$1" in a folder name are inserted literally.
                              .replace("{{folderName}}", () => folderName)
                              .replace("{{count}}", remaining.toString())
                        : props.translate("APP.DIALOG.PROJECT_FILES.FILE_LIMIT_BODY").toString();
                    return {
                        kind,
                        countAtError,
                        intent: "error",
                        message: props.translate("APP.DIALOG.PROJECT_FILES.FILE_LIMIT_TITLE").toString(),
                        info,
                    };
                }
                case "duplicate":
                    return {
                        kind,
                        countAtError,
                        intent: "error",
                        message: props.translate("SHARED.ATTACHMENTS.DUPLICATE_NAME_FLUENT9").toString(),
                        info: props.translate("SHARED.ATTACHMENTS.DUPLICATE_NAME_FLUENT9_INFO").toString(),
                    };
                case "blockedFileType":
                    return {
                        kind,
                        countAtError,
                        intent: "error",
                        message: props.translate("SHARED.ATTACHMENTS.BLOCKED_FILE_TYPE_FLUENT9").toString(),
                        info: props.translate("SHARED.ATTACHMENTS.BLOCKED_FILE_TYPE_FLUENT9_INFO").toString(),
                    };
                case "fetchError":
                    return {
                        kind,
                        countAtError,
                        intent: "error",
                        message: props.translate("SHARED.ATTACHMENTS.ERROR").toString(),
                    };
            }
        },
        [props.translate, effectiveMaxFiles, isCapAlreadyReached]
    );

    const handleBrowserError = useCallback(
        (kind: ProjectFolderErrorKind, context?: ProjectFolderErrorContext | null) =>
            setCurrentError(mapErrorKind(kind, selectedFilesRef.current.length, context)),
        [mapErrorKind]
    );

    // A limit error means the rejected item was never applied, so any existing
    // selection is still valid and attachable. Attach is only blocked when there is
    // nothing selected or nothing more can be attached at all.
    // Duplicate / fetchError are likewise informational.
    const isAttachDisabled = selectedFiles.length === 0 || isCapAlreadyReached || isProjectsLoading || isBrowserLoading;
    const shouldShowAttachTooltip = selectedFiles.length === 0 && !isCapAlreadyReached;

    // Surface the limit banner immediately when nothing more can be attached.
    useEffect(() => {
        if (props.isOpen && isCapAlreadyReached) {
            setCurrentError(mapErrorKind("fileLimit", 0));
        }
    }, [props.isOpen, isCapAlreadyReached, mapErrorKind]);

    // Any interaction with the list (check/uncheck, navigate, sort) dismisses the current
    // banner — it described the previous action. Runs before the hook processes the
    // interaction, so an error raised by this same action still surfaces afterwards.
    // The cap-already-reached banner is kept: nothing the user does in the list can fix it.
    const handleBrowserInteraction = useCallback(() => {
        if (isCapAlreadyReached) return;
        setCurrentError((current) => {
            if (current) browserClearErrorRef.current?.();
            return null;
        });
    }, [isCapAlreadyReached]);

    const handleSelectionChange = useCallback((files: FileWithId[]) => {
        setSelectedFiles(files);
    }, []);

    useEffect(() => {
        if (!props.isOpen) {
            setSelectedProject(undefined);
            setSelectedFiles([]);
            setShowProjectError(false);
            setCurrentError(null);
        }
    }, [props.isOpen]);

    useEffect(() => {
        if (selectedProject) setShowProjectError(false);
    }, [selectedProject]);

    const handleOpenChange = (_: unknown, data: { open: boolean }) => {
        if (!data.open) props.onClose();
    };

    const handleAttach = () => {
        if (!selectedProject) {
            setShowProjectError(true);
            return;
        }
        props.onAttach(selectedFiles);
    };

    const attachButtonLabel =
        selectedFiles.length > 0
            ? props
                  .translate("APP.DIALOG.PROJECT_FILES.ATTACH")
                  .toString()
                  .replace("{{count}}", selectedFiles.length.toString())
            : props
                  .translate("APP.DIALOG.PROJECT_FILES.ATTACH")
                  .toString()
                  .replace(" ({{count}})", "");

    const remainingCount = Math.max(0, effectiveMaxFiles - selectedFiles.length);
    const remainingCountLabel =
        remainingCount === 1
            ? props.translate("APP.DIALOG.PROJECT_FILES.REMAINING_FILES_ONE").toString()
            : props
                  .translate("APP.DIALOG.PROJECT_FILES.REMAINING_FILES_OTHER")
                  .toString()
                  .replace("{{count}}", remainingCount.toString());

    return (
        <Dialog open={props.isOpen} onOpenChange={handleOpenChange}>
            <DialogSurface
                className={styles.surface}
                style={{ width: "calc(100% - 20px)", maxWidth: "479px", minWidth: "unset" }}
                data-testid="project-files-dialog"
            >
                <DialogBody>
                    <DialogTitle
                        className={styles.title}
                        action={
                            <Button
                                data-testid="project-files-dialog-close-button"
                                appearance="subtle"
                                aria-label={props.translate("APP.DIALOG.PROJECT_FILES.CLOSE").toString()}
                                icon={<Dismiss24Regular />}
                                onClick={props.onClose}
                            />
                        }
                    >
                        {props.translate("APP.DIALOG.PROJECT_FILES.TITLE").toString()}
                    </DialogTitle>
                    <DialogContent className={styles.content}>
                        {!isCapAlreadyReached && !currentError && (
                            <span className={styles.remainingCount} data-testid="project-files-dialog-remaining-count">
                                {remainingCountLabel}
                            </span>
                        )}
                        {currentError && (
                            <div
                                key={currentError.kind}
                                className={styles.errorBarWrapper}
                                data-testid="project-files-dialog-error-bar"
                            >
                                <NewformaMessageBar
                                    intent={currentError.intent}
                                    body={
                                        <>
                                            <span className={styles.messageBarTitle}>{currentError.message}</span>
                                            {currentError.info && (
                                                <span className={styles.messageBarInfo}>{currentError.info}</span>
                                            )}
                                        </>
                                    }
                                    onDismiss={
                                        isCapAlreadyReached && currentError.kind === "fileLimit"
                                            ? undefined
                                            : () => {
                                                  setCurrentError(null);
                                                  browserClearErrorRef.current?.();
                                              }
                                    }
                                />
                            </div>
                        )}
                        <ProjectPickerFluent9
                            smartFilingManager={props.smartFilingManager}
                            invalidateCacheService={props.invalidateCacheService}
                            logger={props.logger}
                            officeWrapper={props.officeWrapper}
                            autoSelectSuggested={false}
                            showValidationError={showProjectError}
                            selectedProject={selectedProject}
                            onProjectChange={setSelectedProject}
                            onReloadProjects={() => {
                                reloadPendingRef.current = true;
                            }}
                        />
                        <ProjectFilesBrowser
                            selectedProject={selectedProject}
                            projectsService={props.projectsService}
                            onSelectionChange={handleSelectionChange}
                            onError={handleBrowserError}
                            maxFiles={effectiveMaxFiles}
                            resetKey={browserResetKey}
                            disabled={isProjectsLoading}
                            existingFileIds={props.existingFileIds}
                            onClearError={handleBrowserClearError}
                            onLoadingChange={setIsBrowserLoading}
                            onInteraction={handleBrowserInteraction}
                        />
                    </DialogContent>
                    <DialogActions className={styles.actions}>
                        {shouldShowAttachTooltip ? (
                            <Tooltip
                                content={props.translate("APP.DIALOG.PROJECT_FILES.ATTACH_DISABLED_TOOLTIP").toString()}
                                relationship="label"
                                appearance="inverted"
                                withArrow
                            >
                                <span className={styles.tooltipTarget}>
                                    <Button
                                        data-testid="project-files-dialog-attach-button"
                                        appearance="primary"
                                        className={styles.attachButton}
                                        onClick={handleAttach}
                                        disabled={isAttachDisabled}
                                    >
                                        {attachButtonLabel}
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <Button
                                data-testid="project-files-dialog-attach-button"
                                appearance="primary"
                                className={styles.attachButton}
                                onClick={handleAttach}
                                disabled={isAttachDisabled}
                            >
                                {attachButtonLabel}
                            </Button>
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
});
